import { Tag } from '@components';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Document, Trash } from 'iconsax-react';
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { useContext, useState } from 'react';

import { ATTACHMENT_WARNING_RATIO, MAX_ATTACHMENT_BYTES } from '@core/constants/app';
import { SlabContext } from '@core/context/SlabContext';
import type { AttachmentFileFormikEntry } from '@core/formik/email';
import Enums from '@core/types/api/Enums';
import type { Base64 } from '@core/utils/base64';
import { base64DecodedByteCount, uint8ArrayToBase64 } from '@core/utils/base64';
import { formatAttachmentBytes } from '@core/utils/emailAttachments';
import { validateAttachmentFile } from '@core/utils/fileAttachments';

import { FileDropzone } from '../Files/FileDropzone';

/** Rejection reason for a single file in a multi-file selection. */
type SelectionError = {
  fileName: string;
  message: string;
};

const readFileAsBase64 = async (file: File): Promise<Base64> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      resolve(uint8ArrayToBase64(new Uint8Array(reader.result as ArrayBuffer)));
    };
    reader.onerror = (): void => {
      reject(new Error(`Could not read ${file.name}.`));
    };
    reader.readAsArrayBuffer(file);
  });

type AttachmentRowProps = {
  fileName: string;
  /** Right-hand slot: the "Auto" chip for the quote PDF, a remove button otherwise. */
  action?: ReactNode;
  /** Formatted size, or a placeholder while the PDF is still being generated. */
  size: string;
};

const AttachmentRow: FC<AttachmentRowProps> = ({ fileName, size, action }) => {
  const theme = useTheme();

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      gap='0.75rem'
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: theme.palette.SlabGray[300],
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box display='flex' gap='0.75rem'>
        <Box sx={{ top: 4, position: 'relative', left: 2 }}>
          <Document variant='TwoTone' color={theme.palette.SlabGray[600]} size={20} />
        </Box>

        <Box flex={1} minWidth={0}>
          <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
            {fileName}
          </Typography>
          <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
            {size}
          </Typography>
        </Box>
      </Box>
      {action}
    </Box>
  );
};

type Props = {
  pdfBase64: Base64;
  pdfFileName: string;
  additionalFiles: AttachmentFileFormikEntry[];
  onFilesChange: Dispatch<SetStateAction<AttachmentFileFormikEntry[]>>;
};

export const EmailAttachmentsSection: FC<Props> = ({
  pdfBase64,
  pdfFileName,
  additionalFiles,
  onFilesChange,
}) => {
  const theme = useTheme();
  const ctx = useContext(SlabContext);
  const [selectionErrors, setSelectionErrors] = useState<SelectionError[]>([]);

  const flagEnabled = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagQuoteEmailAttachments,
  ]);
  if (!flagEnabled) {
    return null;
  }

  const pdfBytes = base64DecodedByteCount(pdfBase64);
  const additionalBytes = additionalFiles.reduce(
    (acc, f) => acc + base64DecodedByteCount(f.file),
    0,
  );
  const totalBytes = pdfBytes + additionalBytes;
  const overLimit = totalBytes > MAX_ATTACHMENT_BYTES;
  const nearLimit = totalBytes > MAX_ATTACHMENT_BYTES * ATTACHMENT_WARNING_RATIO;

  let totalColor = theme.palette.text.primary;
  if (overLimit) {
    totalColor = theme.palette.error.main;
  } else if (nearLimit) {
    totalColor = theme.palette.warning.main;
  }

  const handleFilesSelected = (files: File[]): void => {
    const accepted: File[] = [];
    const errors: SelectionError[] = [];
    // The quote PDF and already-attached files both occupy a name; files
    // accepted earlier in this batch claim theirs as we go.
    const takenNames = new Set([pdfFileName, ...additionalFiles.map((f) => f.fileName)]);
    // Size check is approximate: we know file.size before encoding, and base64
    // expands by ~33%. Budget is consumed as the batch is walked so a selection
    // can't collectively overshoot the limit.
    let usedBytes = totalBytes;

    files.forEach((file) => {
      const validationError = validateAttachmentFile(file, MAX_ATTACHMENT_BYTES - usedBytes);
      if (validationError !== null) {
        errors.push({ fileName: file.name, message: validationError });
        return;
      }

      if (takenNames.has(file.name)) {
        errors.push({ fileName: file.name, message: 'This file is already attached.' });
        return;
      }

      takenNames.add(file.name);
      usedBytes += file.size;
      accepted.push(file);
    });

    setSelectionErrors(errors);

    if (accepted.length === 0) {
      return;
    }

    // One onFilesChange for the whole batch, and the reads are async: a removal
    // or another selection can land while they're pending. Merge against the
    // latest state (functional update) rather than this render's snapshot, and
    // drop any name a concurrent batch already claimed.
    Promise.all(
      accepted.map(async (file) => ({
        fileName: file.name,
        file: await readFileAsBase64(file),
      })),
    )
      .then((entries) => {
        onFilesChange((prev) => {
          const existingNames = new Set(prev.map((f) => f.fileName));
          const merged = entries.filter((e) => !existingNames.has(e.fileName));
          return [...prev, ...merged];
        });
      })
      .catch((error: Error) => {
        setSelectionErrors([...errors, { fileName: '', message: error.message }]);
      });
  };

  const handleRemove = (fileName: string): void => {
    onFilesChange((prev) => prev.filter((f) => f.fileName !== fileName));
  };

  return (
    <Box display='flex' flexDirection='column' gap='0.75rem'>
      <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
        The quote PDF above will be included automatically.
      </Typography>

      <Box>
        <FileDropzone onFilesSelected={handleFilesSelected} />
        {selectionErrors.length > 0 && (
          <Box role='alert' display='flex' flexDirection='column' gap='0.125rem' mt='0.25rem'>
            {selectionErrors.map((error) => (
              <Typography
                key={`${error.fileName}-${error.message}`}
                variant='caption'
                sx={{ color: theme.palette.error.main }}
              >
                {error.fileName === '' ? error.message : `${error.fileName}: ${error.message}`}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      <Typography
        variant='body2'
        alignSelf='flex-start'
        sx={{
          color: totalColor,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: theme.palette.SlabGray[100],
        }}
        aria-label={`Total attachment size: ${formatAttachmentBytes(totalBytes)} of ${formatAttachmentBytes(MAX_ATTACHMENT_BYTES)}`}
      >
        Total: {formatAttachmentBytes(totalBytes)} / {formatAttachmentBytes(MAX_ATTACHMENT_BYTES)}
      </Typography>

      <Box
        aria-live='polite'
        aria-label='Attached files'
        display='flex'
        flexDirection='column'
        gap='0.5rem'
      >
        <AttachmentRow
          fileName={pdfFileName}
          size={pdfBase64 !== '' ? formatAttachmentBytes(pdfBytes) : 'Calculating…'}
          action={<Tag variant='Secondary' label='Auto' />}
        />

        {additionalFiles.map((f) => (
          <AttachmentRow
            key={f.fileName}
            fileName={f.fileName}
            size={formatAttachmentBytes(base64DecodedByteCount(f.file))}
            action={
              <IconButton
                aria-label={`Remove ${f.fileName}`}
                onClick={() => handleRemove(f.fileName)}
              >
                <Trash variant='TwoTone' color={theme.palette.error.main} size={20} />
              </IconButton>
            }
          />
        ))}
      </Box>
    </Box>
  );
};
